import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import logoImage from "./images/logowide.png";
import Logo from "./images/logo.png";

const gold = "#DBAE6E";

const ComingSoon = ({ targetDate }) => { // Accept targetDate as a prop
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [expired, setExpired] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const diff = targetDate - now;

      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setFadeOut(true);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / 1000 / 60) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      setTimeLeft({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  useEffect(() => {
    if (fadeOut) {
      const timeout = setTimeout(() => {
        setExpired(true);
        navigate("/"); // Redirect to landing page, consistent with App
      }, 1500); // Match fade duration
      return () => clearTimeout(timeout);
    }
  }, [fadeOut, navigate]);

  const metallicFadeVariants = (delay = 0) => ({
    hidden: { opacity: 0, scale: 0.95, filter: "brightness(0.4) blur(2px)" },
    visible: {
      opacity: 1,
      scale: 1,
      filter: "brightness(1) blur(0px)",
      transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay },
    },
  });

  const fadeInVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 1.2, ease: "easeOut", delay: 1.3 },
    },
  };

  const logoGlowVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 0.8,
      transition: {
        duration: 1.8,
        ease: "easeInOut",
        repeat: Infinity,
        repeatType: "mirror",
      },
    },
  };

  const CounterPlate = ({ value, label, id }) => (
    <div className="flex flex-col items-center mx-2" id={`counter-${id}`}>
      <div className="w-16 h-20 bg-gradient-to-br from-[#DBAE6E] to-[#B58B4C] rounded-lg shadow-lg flex items-center justify-center border border-[#EBC68E]">
        <span className="text-3xl font-bold text-black">
          {value.toString().padStart(2, "0")}
        </span>
      </div>
      <span className="text-xs mt-2 uppercase tracking-wider" style={{ color: gold }}>
        {label}
      </span>
    </div>
  );

  const Particle = ({ i }) => {
    const size = Math.random() * 4 + 1;
    const xStart = Math.random() * window.innerWidth;
    const xDrift = Math.random() * 80 - 40;
    const duration = Math.random() * 5 + 3;
    const delay = Math.random() * 4;

    return (
      <motion.div
        key={i}
        className="absolute rounded-full"
        style={{
          width: size,
          height: size,
          backgroundColor: gold,
          filter: `drop-shadow(0 0 2px ${gold})`,
        }}
        initial={{ x: xStart, y: window.innerHeight + 20, opacity: 0 }}
        animate={{
          y: -100,
          x: xStart + xDrift,
          opacity: [0, 0.6, 0],
        }}
        transition={{
          duration,
          repeat: Infinity,
          delay,
          ease: "easeInOut",
        }}
      />
    );
  };

  return (
    <motion.div
      className="h-screen w-screen bg-black flex flex-col justify-center items-center text-white relative overflow-hidden font-sans"
      initial={{ opacity: 1 }}
      animate={{ opacity: fadeOut ? 0 : 1 }}
      transition={{ duration: 1.5 }}
    >
      <AnimatePresence>
        {!expired && (
          <>
            <motion.div
              className="flex flex-col items-center space-y-8 z-10 px-6"
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              <motion.div className="relative" variants={metallicFadeVariants(0)}>
                <motion.img
                  src={Logo}
                  alt="Logo"
                  className="h-32 w-auto"
                  variants={metallicFadeVariants(0)}
                  whileHover={{ scale: 1.05 }}
                />
                <motion.div
                  className="absolute inset-0"
                  style={{
                    boxShadow: `0 0 20px ${gold}`,
                    borderRadius: "50%",
                  }}
                  variants={logoGlowVariants}
                />
              </motion.div>

              <motion.img
                src={logoImage}
                alt="LogoText"
                className="h-12 w-auto drop-shadow-[0_0_15px_rgba(219,174,110,0.4)]"
                variants={metallicFadeVariants(0.4)}
                whileHover={{ scale: 1.05 }}
              />

              <motion.h1
                className="text-5xl font-extrabold tracking-[0.1em] text-center uppercase"
                style={{
                  color: gold,
                  textShadow: `0 0 20px ${gold}b0, 0 0 40px ${gold}50`,
                }}
                variants={metallicFadeVariants(0.8)}
                whileHover={{ scale: 1.05 }}
              >
                Opening
              </motion.h1>

              <motion.div className="flex justify-center space-x-2" variants={fadeInVariants}>
                <CounterPlate id="days" value={timeLeft.days} label="Days" />
                <CounterPlate id="hours" value={timeLeft.hours} label="Hours" />
                <CounterPlate id="minutes" value={timeLeft.minutes} label="Minutes" />
                <CounterPlate id="seconds" value={timeLeft.seconds} label="Seconds" />
              </motion.div>

              <motion.p
                className="text-lg text-center max-w-lg font-light"
                style={{ color: `${gold}ee`, textShadow: `0 0 10px ${gold}50` }}
                variants={metallicFadeVariants(1.7)}
              >
                With our fine premium clothing
              </motion.p>

              <motion.span
                className="text-sm uppercase tracking-[0.3em] opacity-85"
                style={{ color: gold }}
                variants={metallicFadeVariants(2.2)}
                animate={{ opacity: [0.85, 1, 0.85], y: [0, -5, 0] }}
                transition={{ repeat: Infinity, duration: 2.5 }}
              >
                Stay Tuned
              </motion.span>
            </motion.div>

            {[...Array(150)].map((_, i) => <Particle key={i} i={i} />)}
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ComingSoon;